import React, { useState } from "react";
import { Box, Card, Typography, IconButton, Snackbar, Stack } from "@mui/material";
import { BaseButton } from "../../../common/base-button/base-button";
import BaseDialog from "../../../common/base-dialog/base-dialog";
import BaseTextField from "../../../common/base-text-field/base-text-field";
import axios from "axios";
import BASE_URL from "../../../../constants/baseUrls";
import copyIcon from "./copy.png"; // Update with your image path
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

// Custom modal content component (unchanged)
import { useEffect } from "react";
import useUserInfo from "../../../../hooks/useUserInfo";


const InviteModalContent = ({ onClose, onSend }) => {
    const { t } = useTranslation();
    const { userInformation, getUserInfo } = useUserInfo();

    useEffect(() => {
        // Fetch user info when component mounts
        getUserInfo();
    }, []);

    

    const registerOptions = {
        firstName: {
            required: `${t("firstName")} ${t("isRequired")}`,
            minLength: { value: 2, message: t("firstNameValidation1") },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: t("firstNameValidation2") },
        },
        lastName: {
            required: `${t("lastName")} ${t("isRequired")}`,
            minLength: { value: 2, message: t("lastNameValidation1") },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: t("lastNameValidation2") },
        },
        phoneNumber: {
            required: `${t("phoneNumberValidation4")}`,
            maxLength: { value: 11, message: `${t("phoneNumberValidation1")}` },
            minLength: { value: 11, message: `${t("phoneNumberValidation2")}` },
            pattern: { value: /^09\d{9}$/, message: `${t("phoneNumberValidation3")}` },
        },
    };

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors },
    } = useForm();

    // Set form values when userInformation is available
    useEffect(() => {
        if (userInformation) {
            if (userInformation.first_name) {
                setValue("firstName", userInformation.first_name);
            }
            if (userInformation.last_name) {
                setValue("lastName", userInformation.last_name);
            }
        }
    }, [userInformation, setValue]);

    const handleRegistration = data => {
        onSend(data)
    };

    return (
        <Box sx={{ p: 3 }} component="form" onSubmit={handleSubmit(handleRegistration)}>
            <Stack spacing={2}>
                <Typography color="text.secondary">لطفا نام و نام خانوادگی خودت را وارد کن:</Typography>
                <BaseTextField
                    label="نام"
                    dir="rtl"
                    name="firstName"
                    register={{ ...register("firstName", registerOptions.firstName) }}
                    error={errors?.firstName?.message}
                    disabled={!!userInformation?.first_name} // Disable if first_name exists
                    readOnly={!!userInformation?.first_name} // Make read-only if first_name exists
                    focused={!!userInformation?.first_name}
                />
                <BaseTextField
                    label="نام خانوادگی"
                    name="lastName"
                    register={{ ...register("lastName", registerOptions.lastName) }}
                    error={errors?.lastName?.message}
                    dir="rtl"
                    disabled={!!userInformation?.last_name} // Disable if last_name exists
                    readOnly={!!userInformation?.last_name} // Make read-only if last_name exists
                    focused={!!userInformation?.last_name}
                />
                <Typography color="text.secondary" sx={{ pt: 5 }}>
                    لطفاشماره کسی که میخوای دعوت کنی رو وارد کن:
                </Typography>
                <BaseTextField
                    label="شماره همراه"
                    name="phoneNumber"
                    register={{ ...register("phoneNumber", registerOptions.phoneNumber) }}
                    error={errors?.phoneNumber?.message}
                    dir="rtl"
                />
                <BaseButton
                    text="ارسال"
                    color="primary"
                    size="medium"
                    type="submit"
                    radius="radius-10"
                    sx={{ bgcolor: "#006363", color: "white", "&:hover": { bgcolor: "#005353" } }}
                />
            </Stack>
        </Box>
    );
};

export const InviteCodeCard = ({ code }) => {
    const [open, setOpen] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const accessToken = localStorage.getItem("accessToken");

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleCopyClick = () => {
        navigator.clipboard
            .writeText(code)
            .then(() => {
                setOpenSnackbar(true);
            })
            .catch(err => {
                console.error("Failed to copy text: ", err);
            });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpenSnackbar(false);
    };

    const handleSendInvite = async data => {
        console.log(data);
        
        try {
            setIsLoading(true);
            console.log("Sending invite data:", data);

            const requestBody = {
                phone_number: data.phoneNumber,
                first_name: data.firstName,
                last_name: data.lastName,
            };

            const response = await axios.post(`${BASE_URL}/api/v2/account/user/send-invitation/`, requestBody, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            console.log("Invitation sent successfully:", response.data);
            toast.success(response.data.message);
        } catch (error) {
            console.error("Error sending invitation:", error);
            toast.error(error.response.data.errors.detail[0], { toastId: "custom-id-yes" });
        } finally {
            setIsLoading(false);
            handleClose();
        }
    };

    return (
        <>
            <Card
                variant="outlined"
                sx={{
                    p: { xs: 1.5, sm: 2 },
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                    width: "100%",
                    flexDirection: { xs: "column", sm: "row" },
                }}
            >
                <Box
                    sx={{
                        border: "1px solid #006363",
                        borderRadius: 1,
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.75, sm: 1 },
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 0.5, sm: 1 },
                        width: "100%",
                        minWidth: 0,
                        flexDirection: { xs: "column", sm: "row" },
                        textAlign: { xs: "center", sm: "left" },
                    }}
                >
                    <Typography
                        color="text.secondary"
                        sx={{
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                            mb: { xs: 0.5, sm: 0 },
                        }}
                    >
                        کد دعوت شما
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                            fontWeight="bold"
                            color="#006363"
                            sx={{
                                fontSize: { xs: "1rem", sm: "1.125rem" },
                            }}
                        >
                            {code}
                        </Typography>
                        <IconButton onClick={handleCopyClick} sx={{ p: 0.5 }}>
                            <Box
                                component="img"
                                src={copyIcon}
                                alt="Copy"
                                sx={{
                                    width: 20,
                                    height: 20,
                                    "&:hover": {
                                        opacity: 0.8,
                                    },
                                }}
                            />
                        </IconButton>
                    </Box>
                </Box>
                <BaseButton
                    text={"ارسال دعوت‌نامه"}
                    color="primary"
                    size="medium"
                    radius="radius-10"
                    handleClick={handleOpen}
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                />
            </Card>

            <BaseDialog open={open} handleClose={handleClose} title="ارسال دعوتنامه" maxWidth="xs" top="auto" icon="close">
                <InviteModalContent onClose={handleClose} onSend={handleSendInvite} />
            </BaseDialog>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                message="کد دعوت کپی شد!"
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </>
    );
};
